import { unreachable } from "common/functions";
import { cancel, Waiter } from "common/promises";
export function getMaxClicks(speed) {
    switch (speed) {
        case 1:
            return 4;
        case 2:
            return 10;
        case 3:
            return 15;
        case 4:
            return 15;
    }
}
class ClickedListeners {
    constructor() {
        this.listeners = [];
    }
    clicked() {
        for (const listener of this.listeners) {
            listener.clicked();
        }
    }
    add(listener) {
        this.listeners.push(listener);
    }
}
class WaitingButtonListeners {
    constructor() {
        this.listeners = [];
    }
    waitOn(button) {
        for (const listener of this.listeners) {
            listener.waitOn(button);
        }
    }
    add(listener) {
        this.listeners.push(listener);
    }
}
export class ButtonInputFilter {
    constructor(pixiContext, aButton, bButton) {
        this.aWaiter = new Waiter();
        this.bWaiter = new Waiter();
        this.clickedListeners = new ClickedListeners();
        this.waitingButtonListeners = new WaitingButtonListeners();
        this.pixiContext = pixiContext;
        this.aClick = this.aClick.bind(this);
        this.bClick = this.bClick.bind(this);
        this.lastClick = "none";
        aButton.setCallbak(this.aClick);
        bButton.setCallbak(this.bClick);
        this.state = { id: "idle" };
    }
    async run(onStateChange) {
        let stateResult = await this.runState(onStateChange);
        while (stateResult.newState != null) {
            stateResult = await stateResult.newState;
        }
    }
    async runState(onStateChange, startSpeed = 1) {
        this.stateChange({ id: "run", speed: startSpeed, clickCount: 0 }, onStateChange);
        const result = await this.continueRun(onStateChange);
        if (result === "finish") {
            return {
                newState: this.finishState(onStateChange),
            };
        }
        else {
            throw new Error("Invalid run result " + result);
        }
    }
    async finishState(onStateChange) {
        this.stateChange({ id: "finish" }, onStateChange);
        return {
            newState: null,
        };
    }
    aClick() {
        this.aWaiter.trigger();
        this.aWaiter.reset();
        this.waitingButtonListeners.waitOn("B");
    }
    bClick() {
        this.bWaiter.trigger();
        this.bWaiter.reset();
        this.waitingButtonListeners.waitOn("A");
    }
    async awaitAClick(canceler) {
        await this.aWaiter.wait();
        if (canceler.canceled) {
            return;
        }
        this.lastClick = "A";
        this.clickedListeners.clicked();
    }
    async awaitBClick(canceler) {
        await this.bWaiter.wait();
        if (canceler.canceled) {
            return;
        }
        canceler.check();
        this.lastClick = "B";
        this.clickedListeners.clicked();
    }
    async awaitAnyClick(canceler) {
        const clicked = await Promise.race([
            this.aWaiter.wait().then(() => "A"),
            this.bWaiter.wait().then(() => "B"),
        ]);
        canceler.check();
        this.lastClick = clicked;
        this.clickedListeners.clicked();
    }
    async continueRun(onStateChange) {
        while (true) {
            let nextSpeed = 0;
            if (this.state.id !== "run") {
                unreachable();
            }
            switch (this.state.speed) {
                case 1:
                    nextSpeed = await this.run1(onStateChange);
                    break;
                case 2:
                    nextSpeed = await this.run2(onStateChange);
                    break;
                case 3:
                    nextSpeed = await this.run3(onStateChange);
                    break;
                case 4:
                    const finishOrSpeed = await this.run4(onStateChange);
                    if (finishOrSpeed === "finish") {
                        return "finish";
                    }
                    else {
                        nextSpeed = finishOrSpeed;
                    }
                    break;
            }
            const newState = Object.assign(Object.assign({}, this.state), { speed: nextSpeed, clickCount: 0 });
            this.stateChange(newState, onStateChange);
        }
    }
    async run1(onStateChange) {
        const countClicks = cancel.root(async (canceler) => {
            await this.countClicksUntil(canceler, getMaxClicks(1), onStateChange);
        });
        const noClickFor = cancel.root(async (canceler) => await this.noClickFor(canceler, 1000));
        const result = await Promise.race([
            noClickFor.promise.then(() => "noinput"),
            countClicks.promise.then(() => "clicked"),
        ]);
        countClicks.canceler.cancel();
        noClickFor.canceler.cancel();
        switch (result) {
            case "noinput":
                return 1;
            case "clicked":
                return 2;
        }
    }
    async run2(onStateChange) {
        const countClicks = cancel.root(async (canceler) => {
            await this.countClicksUntil(canceler, getMaxClicks(2), onStateChange);
        });
        const noClickFor = cancel.root(async (canceler) => await this.noClickFor(canceler, 1000));
        const result = await Promise.race([
            noClickFor.promise.then(() => "noinput"),
            countClicks.promise.then(() => "clicked"),
        ]);
        countClicks.canceler.cancel();
        noClickFor.canceler.cancel();
        switch (result) {
            case "noinput":
                return 1;
            case "clicked":
                return 3;
        }
    }
    async run3(onStateChange) {
        const countClicks = cancel.root(async (canceler) => {
            await this.countClicksUntil(canceler, getMaxClicks(3), onStateChange);
        });
        const noClickFor = cancel.root(async (canceler) => await this.noClickFor(canceler, 1000));
        const result = await Promise.race([
            noClickFor.promise.then(() => "noinput"),
            countClicks.promise.then(() => "clicked"),
        ]);
        countClicks.canceler.cancel();
        noClickFor.canceler.cancel();
        switch (result) {
            case "noinput":
                return 2;
            case "clicked":
                return 4;
        }
    }
    async run4(onStateChange) {
        const countClicks = cancel.root(async (canceler) => {
            await this.countClicksUntil(canceler, getMaxClicks(4), onStateChange);
        });
        const noClickFor = cancel.root(async (canceler) => this.noClickFor(canceler, 1000));
        const result = await Promise.race([
            noClickFor.promise.then(() => "noinput"),
            countClicks.promise.then(() => "clicked"),
        ]);
        countClicks.canceler.cancel();
        noClickFor.canceler.cancel();
        switch (result) {
            case "noinput":
                return 3;
            case "clicked":
                return "finish";
        }
    }
    async noClickFor(canceler, millis) {
        while (true) {
            canceler.check();
            const result = await Promise.race([
                this.aWaiter.wait().then(() => "a"),
                this.bWaiter.wait().then(() => "b"),
                this.pixiContext.delay(millis).then(() => "timeout"),
            ]);
            if (result === "timeout") {
                return;
            }
        }
    }
    async countClicksUntil(canceler, count, onStateChange) {
        while (true) {
            canceler.check();
            if (count === 0) {
                return;
            }
            if (this.lastClick === "A") {
                await this.awaitBClick(canceler);
                count -= 1;
            }
            else if (this.lastClick === "B") {
                await this.awaitAClick(canceler);
                count -= 1;
            }
            else {
                await this.awaitAnyClick(canceler);
                count -= 1;
            }
            canceler.check();
            if (this.state.id === "run") {
                // this.state.clickCount += 1;
                this.stateChange(Object.assign(Object.assign({}, this.state), { clickCount: this.state.clickCount + 1 }), onStateChange);
            }
        }
    }
    async countClicks(canceler, clicks) {
        clicks.count = 0;
        while (true) {
            canceler.check();
            if (this.lastClick === "A") {
                await this.awaitBClick(canceler);
                clicks.count += 1;
            }
            else if (this.lastClick === "B") {
                await this.awaitAClick(canceler);
                clicks.count += 1;
            }
            else {
                await this.awaitAnyClick(canceler);
                clicks.count += 1;
            }
        }
    }
    stateChange(to, onStateChange) {
        const prev = this.state;
        this.state = to;
        console.log(`Move to ${JSON.stringify(to)} from ${JSON.stringify(prev)}`);
        onStateChange({ from: prev, to: this.state });
    }
}
//# sourceMappingURL=buttonInputFilter.js.map